/**
 * Shared grade-band picker ('K-2' | '3-5' | '6-8' | '9-12' | 'all').
 * Parent translates to gradeMin/gradeMax via bandToRange(); 'all' returns
 * nulls — the caller must OMIT both fields (intent-first spec §3 Zone 3:
 * explicit 0/12 would override prose-derived grades and unfence the IIG
 * concept resolution — never send it).
 *
 * `restored` renders the value visually distinct (dashed border) until the
 * teacher interacts — a sticky default from a previous session must never
 * look like a fresh choice (adversarial finding: stale bands silently
 * produced wrong-grade posters).
 */

import { useTranslation } from 'react-i18next';

export const GRADE_BANDS = [
  { label: 'K-2',  min: 0,  max: 2  },
  { label: '3-5',  min: 3,  max: 5  },
  { label: '6-8',  min: 6,  max: 8  },
  { label: '9-12', min: 9,  max: 12 },
];

export const ALL_GRADES = 'all';

export function bandToRange(label) {
  const found = GRADE_BANDS.find((b) => b.label === label);
  return found ? { gradeMin: found.min, gradeMax: found.max } : { gradeMin: null, gradeMax: null };
}

export default function GradeBandPicker({ value, onChange, label, required = false, allowAll = false, restored = false, bare = false }) {
  const { t } = useTranslation('home');
  const labelText = label ?? t('gradeBand.label');
  const bands = allowAll ? [...GRADE_BANDS, { label: ALL_GRADES }] : GRADE_BANDS;
  // Band labels are BOTH stored values and display text — stored values stay
  // as-is; display translates via this map (unknown value → raw passthrough).
  const bandDisplay = {
    'K-2': t('gradeBand.bands.k2'),
    '3-5': t('gradeBand.bands.g3to5'),
    '6-8': t('gradeBand.bands.g6to8'),
    '9-12': t('gradeBand.bands.g9to12'),
    [ALL_GRADES]: t('gradeBand.allGrades'),
  };
  return (
    <div>
      {labelText && !bare && (
        <div className="bv-aigrades__head">
          <span className="bv-label">{labelText}</span>
          <span className="bv-small bv-muted">{required ? t('gradeBand.required') : t('gradeBand.optional')}</span>
          {restored && value && <span className="bv-small bv-aigrades__restored">{t('gradeBand.restoredHint')}</span>}
        </div>
      )}
      {bare && restored && value && <div className="bv-small bv-aigrades__restored">{t('gradeBand.restoredHint')}</div>}
      <div className="bv-aigrades" role="group" aria-label={labelText}>
        {bands.map((band) => {
          const active = value === band.label;
          return (
            <button
              key={band.label}
              type="button"
              className={`bv-aigrade${active ? (restored ? ' is-restored' : ' is-on') : ''}`}
              onClick={() => onChange(required && active ? band.label : active ? null : band.label)}
              title={active && !required ? t('gradeBand.clearHint') : undefined}
              aria-pressed={active}
            >
              {bandDisplay[band.label] ?? band.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
